import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminSessionManager.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * AdminSessionManager Component
 * 
 * Organization-wide session management for admins:
 * - View all active sessions across all users in org
 * - Force-end any user's session
 * - See session statistics (total sessions, active users)
 * - Group sessions by user for easy management
 */
const AdminSessionManager = () => {
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forcingEnd, setForcingEnd] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('veripass_token');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const url = `${API_BASE}/api/v1/admin/sessions${includeInactive ? '?include_inactive=true' : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchSessions();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const handleForceEnd = async (sessionId, userName) => {
    if (!window.confirm(`Force-end session for ${userName}? They will be logged out immediately.`)) {
      return;
    }

    setForcingEnd(sessionId);

    try {
      const token = localStorage.getItem('veripass_token');
      await axios.post(
        `${API_BASE}/api/v1/admin/sessions/${sessionId}/force-end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh sessions list
      await fetchSessions();
    } catch (err) {
      console.error('Failed to force-end session:', err);
      alert(err.response?.data?.detail || 'Failed to end session');
    } finally {
      setForcingEnd(null);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return '📱';
      case 'tablet': return '📲';
      case 'desktop':
      default: return '💻';
    }
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const now = new Date();
    const lastActivity = new Date(timestamp);
    const diffMs = now - lastActivity;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="admin-session-manager">
        <div className="loading">Loading organization sessions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-session-manager">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const { sessions, stats } = sessionData || { sessions: [], stats: {} };

  return (
    <div className="admin-session-manager">
      <div className="session-header">
        <div className="header-left">
          <h2>Organization Sessions</h2>
          <div className="header-stats">
            <span className="stat-badge active">
              {stats.active_sessions || 0} Active
            </span>
            <span className="stat-badge users">
              {stats.active_users || 0} Users
            </span>
            {stats.total_sessions > stats.active_sessions && (
              <span className="stat-badge inactive">
                {(stats.total_sessions - stats.active_sessions) || 0} Inactive
              </span>
            )}
          </div>
        </div>
        
        <div className="header-right">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>Show inactive sessions</span>
          </label>
          <button className="btn-refresh" onClick={fetchSessions}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="no-sessions">No active sessions in organization</div>
      ) : (
        <div className="users-list">
          {sessions.map((userGroup) => (
            <div key={userGroup.user_id} className="user-group">
              <div className="user-header">
                <div className="user-info">
                  <h3>{userGroup.user_name || 'Unknown User'}</h3>
                  <span className="user-email">{userGroup.user_email}</span>
                </div>
                <div className="session-badge">
                  {userGroup.sessions.filter(s => s.is_active).length} / 3 sessions
                </div>
              </div>

              <div className="sessions-grid">
                {userGroup.sessions.map((session) => (
                  <div 
                    key={session.session_id}
                    className={`session-card ${!session.is_active ? 'inactive' : ''}`}
                  >
                    <div className="session-icon">
                      {getDeviceIcon(session.device_type)}
                    </div>
                    
                    <div className="session-details">
                      <div className="session-device">
                        <strong>{session.device_name || 'Unknown Device'}</strong>
                        {!session.is_active && (
                          <span className="inactive-badge">Inactive</span>
                        )}
                      </div>
                      
                      <div className="session-info">
                        {session.browser && <span>{session.browser}</span>}
                        {session.os && <span> • {session.os}</span>}
                      </div>
                      
                      <div className="session-meta">
                        <span className="activity">
                          Last active: {formatLastActivity(session.last_activity_at)}
                        </span>
                        {session.ip_address && (
                          <span className="ip"> • {session.ip_address}</span>
                        )}
                        {session.city && session.country && (
                          <span className="location"> • {session.city}, {session.country}</span>
                        )}
                      </div>
                    </div>

                    {session.is_active && (
                      <button
                        className="btn-force-end"
                        onClick={() => handleForceEnd(session.session_id, userGroup.user_name)}
                        disabled={forcingEnd === session.session_id}
                      >
                        {forcingEnd === session.session_id ? 'Ending...' : 'Force End'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSessionManager;
